import { cloneDeep } from "lodash-es"
import { buildTradeItemInputsFromBlocks } from "~/utils/tradeCommodityItems"

export type ReceiptGradeItem = {
  grade?: any
  rate?: number
  quantity?: number | string
  [key: string]: any
}

export type ReceiptCommodityBlock = {
  commodity?: any
  commodity_variety?: any
  plot_crop?: any
  quantity_unit?: any
  grades: ReceiptGradeItem[]
  supplier_sgst?: number
  supplier_cgst?: number
  supplier_igst?: number
  supplier_sgst_percentage?: number
  supplier_cgst_percentage?: number
  supplier_igst_percentage?: number
  supplier_gst_auto_calculated?: boolean
  _gstFromBackend?: boolean
  seller_gst_attachments?: any[]
}

export function createEmptyReceiptCommodityBlock(
  partial?: Partial<ReceiptCommodityBlock>
): ReceiptCommodityBlock {
  return {
    grades: [{ grade: null, rate: 0, quantity: 0 }],
    supplier_sgst: 0,
    supplier_cgst: 0,
    supplier_igst: 0,
    supplier_sgst_percentage: 0,
    supplier_cgst_percentage: 0,
    supplier_igst_percentage: 0,
    supplier_gst_auto_calculated: true,
    _gstFromBackend: false,
    seller_gst_attachments: [],
    ...partial,
  }
}

export function getCommodityParticularsTotals(block: ReceiptCommodityBlock) {
  const grades = block.grades?.length
    ? block.grades
    : [{ grade: null, rate: 0, quantity: 0 }]
  return grades.reduce<{ quantity: number; total_cost: number }>(
    (acc, g) => {
      const qty = Number(g.quantity) || 0
      const rate = Number(g.rate) || 0
      return {
        quantity: acc.quantity + qty,
        total_cost: acc.total_cost + qty * rate,
      }
    },
    { quantity: 0, total_cost: 0 }
  )
}

export function getBuyerParticularsStats(trade: any) {
  const blocks: ReceiptCommodityBlock[] =
    trade?.commodity_items?.length > 0 ? trade.commodity_items : []
  let items = 0
  let qty = 0
  let cost = 0
  blocks.forEach(block => {
    const totals = getCommodityParticularsTotals(block)
    items += block.grades?.length || 0
    qty += totals.quantity
    cost += totals.total_cost
  })
  return { items, qty, cost }
}

export function buildReceiptTradeItemInputsFromBlocks(
  blocks: ReceiptCommodityBlock[],
  opts: { defaultCurrency?: { id?: any }; includeId?: boolean }
): any[] {
  const tradeBlocks = blocks.map(block => ({
    commodity: block.commodity,
    commodity_variety: block.commodity_variety,
    plot_crop: block.plot_crop,
    quantity_unit: block.quantity_unit,
    grades: (block.grades || []).map(grade => ({
      ...grade,
      variety_grade: grade.grade,
    })),
    gst: {
      supplier_sgst: block.supplier_sgst || 0,
      supplier_cgst: block.supplier_cgst || 0,
      supplier_igst: block.supplier_igst || 0,
      supplier_sgst_percentage: block.supplier_sgst_percentage || 0,
      supplier_cgst_percentage: block.supplier_cgst_percentage || 0,
      supplier_igst_percentage: block.supplier_igst_percentage || 0,
      supplier_gst_auto_calculated: block.supplier_gst_auto_calculated ?? true,
      seller_gst_attachments: block.seller_gst_attachments || [],
      buyer_sgst: 0,
      buyer_cgst: 0,
      buyer_igst: 0,
      buyer_sgst_percentage: 0,
      buyer_cgst_percentage: 0,
      buyer_igst_percentage: 0,
      buyer_gst_auto_calculated: true,
      buyer_gst_attachments: [],
      _gstFromBackend: block._gstFromBackend ?? false,
    },
  }))

  return buildTradeItemInputsFromBlocks(tradeBlocks, {
    defaultCurrency: opts.defaultCurrency,
    includeId: opts.includeId ?? true,
    parentTradeItemIdFor: grade => grade.parent_trade_item_id,
  })
}

export function syncReceiptFormValues(state: any): any {
  const stateCopy = cloneDeep(state)
  let aggSupplierSgst = 0
  let aggSupplierCgst = 0
  let aggSupplierIgst = 0
  let aggSupplierSgstPct = 0
  let aggSupplierCgstPct = 0
  let aggSupplierIgstPct = 0
  let aggAttachments: any[] = []
  let aggAutoCalc = true
  let aggFromBackend = false

  stateCopy.trades?.forEach((trade: any) => {
    if (!trade.commodity_items?.length) {
      return
    }
    const flatItems: ReceiptGradeItem[] = []
    trade.commodity_items.forEach((block: ReceiptCommodityBlock) => {
      const grades = block.grades?.length
        ? block.grades
        : [{ grade: null, rate: 0, quantity: 0 }]
      grades.forEach(grade =>
        flatItems.push({ ...grade, quantity_unit: block.quantity_unit })
      )

      aggSupplierSgst += block.supplier_sgst || 0
      aggSupplierCgst += block.supplier_cgst || 0
      aggSupplierIgst += block.supplier_igst || 0
      aggSupplierSgstPct += block.supplier_sgst_percentage || 0
      aggSupplierCgstPct += block.supplier_cgst_percentage || 0
      aggSupplierIgstPct += block.supplier_igst_percentage || 0
      if (block.seller_gst_attachments?.length) {
        aggAttachments = [...aggAttachments, ...block.seller_gst_attachments]
      }
      if (block.supplier_gst_auto_calculated === false) aggAutoCalc = false
      if (block._gstFromBackend) aggFromBackend = true
    })
    trade.trade_items = flatItems.length ? flatItems : trade.trade_items
  })

  stateCopy.supplier_sgst = aggSupplierSgst
  stateCopy.supplier_cgst = aggSupplierCgst
  stateCopy.supplier_igst = aggSupplierIgst
  stateCopy.supplier_sgst_percentage = aggSupplierSgstPct
  stateCopy.supplier_cgst_percentage = aggSupplierCgstPct
  stateCopy.supplier_igst_percentage = aggSupplierIgstPct
  stateCopy.seller_gst_attachments = aggAttachments
  stateCopy.supplier_gst_auto_calculated = aggAutoCalc
  stateCopy._gstFromBackend = aggFromBackend

  return stateCopy
}
